import React, { useMemo, useState } from "react";

import { multipleChoiceConfirmWrapper } from "../../../../../components/Modals/MultipleChoiceConfirmWrapper";
import ChevronDownIcon from "../../../../../resources/route-builder/chevron-down.svg";
import TrashIcon from "../../../../../resources/route-builder/trash-lid.svg";
import { useDraftsList } from "../hooks/useDraftsList";
import { formatDraftMaxDuration } from "../lib/helpers";

function formatDraftSavedAt(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** Groups drafts accordion: compact by default, searchable scroll list when open. */
export default function DraftsList({
  companyId,
  onOpenDraft,
}: {
  companyId: string;
  onOpenDraft: (draftId: string) => void;
}) {
  const { drafts, isLoading, isDeleting, deleteDraft } =
    useDraftsList(companyId);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filteredDrafts = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return drafts;
    return drafts.filter((draft) => draft.name.toLowerCase().includes(needle));
  }, [drafts, query]);

  if (isLoading || drafts.length === 0) {
    return null;
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/80">
      <button
        type="button"
        data-testid="route-builder-drafts-toggle"
        aria-expanded={open}
        aria-controls="route-builder-drafts-list"
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">Drafts</span>
          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
            {drafts.length}
          </span>
        </span>
        <img
          src={ChevronDownIcon}
          alt=""
          className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>

      {open ? (
        <div className="border-t border-slate-200 px-1.5 pb-1.5 pt-1">
          {drafts.length > 5 || query ? (
            <input
              data-testid="route-builder-drafts-search"
              type="search"
              value={query}
              aria-label="Search drafts"
              placeholder="Search drafts"
              className="mb-1.5 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-snow-blue-3"
              onChange={(event) => setQuery(event.target.value)}
            />
          ) : null}
          <ul
            id="route-builder-drafts-list"
            className="max-h-[min(16rem,40dvh)] list-none overflow-y-auto"
            data-testid="route-builder-drafts-list"
          >
            {filteredDrafts.length === 0 ? (
              <li className="px-2 py-1.5 text-sm text-slate-500">
                No drafts match “{query.trim()}”
              </li>
            ) : (
              filteredDrafts.map((draft) => (
                <li key={draft.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    data-testid={`route-builder-draft-${draft.id}`}
                    disabled={isDeleting}
                    className="flex min-w-0 flex-1 flex-col rounded-lg px-2 py-1.5 text-left hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => void onOpenDraft(draft.id)}
                  >
                    <span className="truncate text-sm font-semibold text-snow-blue-3">
                      {draft.name}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {formatDraftMaxDuration(draft.inputs)}
                      Saved {formatDraftSavedAt(draft.savedAt)}
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid={`route-builder-delete-draft-${draft.id}`}
                    aria-label={`Delete draft ${draft.name}`}
                    title={`Delete draft ${draft.name}`}
                    disabled={isDeleting}
                    className="shrink-0 rounded p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={async () => {
                      const ok = await multipleChoiceConfirmWrapper(
                        "Delete draft?",
                        {
                          subtitle: `Delete “${draft.name}”? This cannot be undone.`,
                          confirmLabel: "Delete",
                        },
                      );
                      if (!ok) return;
                      try {
                        await deleteDraft(draft.id);
                      } catch {
                        // The mutation already reports the failure.
                      }
                    }}
                  >
                    <img
                      src={TrashIcon}
                      alt=""
                      className="h-4 w-4"
                      aria-hidden
                    />
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
